import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Search, FileDown, Upload } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Table } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { ExcelImportModal } from '@/components/ui/ExcelImportModal';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { apiError } from '@/lib/apiError';
import { formatINR } from '@/lib/format';
import { downloadExcel } from '@/lib/downloadExcel';
import {
  listGroups, listLedgers, createLedger, alterLedger, deleteLedger, getLedgerVouchers, importOpening,
  OPENING_TEMPLATE_URL, dmy, type AccLedger, listCostCentres, listTdsSections,
} from '@/api/accountsBooks';
import { groupTree } from './groupTree';

/** Signed amount (+ Dr, − Cr) → "₹1,25,000 Dr". */
const drcr = (n: number) => (n ? `₹${formatINR(Math.abs(n))} ${n > 0 ? 'Dr' : 'Cr'}` : '—');

const label = 'mb-1 block text-xs font-semibold text-[var(--text-secondary)]';
const sideBtn =
  'inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:gap-1.5 sm:px-3 sm:text-xs';

interface Draft {
  id: number | null; name: string; alias: string; groupId: string; opening: string; side: 'Dr' | 'Cr'; partyId: number | null;
  /** Expense heads: the expense bill's defaults. */
  gstRate: string; tdsSection: string; costCentreId: string; itcBlocked: string;
}
const NO_DEFAULTS = { gstRate: '', tdsSection: '', costCentreId: '', itcBlocked: '' };

export function LedgersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const [search, setSearch] = useState('');
  const [groupId, setGroupId] = useState('');
  const [withBalance, setWithBalance] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [viewing, setViewing] = useState<AccLedger | null>(null);

  const query = useQuery({ queryKey: ['acc-books', 'ledgers'], queryFn: listLedgers });
  const groupsQ = useQuery({ queryKey: ['acc-books', 'groups'], queryFn: listGroups });
  const tree = useMemo(() => groupTree(groupsQ.data ?? []), [groupsQ.data]);
  const all = useMemo(() => query.data ?? [], [query.data]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((l) =>
      (!groupId || l.groupId === Number(groupId)) &&
      (!withBalance || l.closing !== 0) &&
      (!q || [l.name, l.alias, l.partyCode, l.group].some((s) => (s ?? '').toLowerCase().includes(q))),
    );
  }, [all, search, groupId, withBalance]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['acc-books', 'ledgers'] });
    qc.invalidateQueries({ queryKey: ['acc-books', 'ledger-options'] });
  };

  const saveMut = useMutation({
    mutationFn: (d: Draft) => {
      const body = {
        name: d.name.trim(), alias: d.alias.trim() || null, groupId: Number(d.groupId), opening: Number(d.opening || 0), side: d.side,
        gstRate: d.gstRate === '' ? null : Number(d.gstRate), tdsSection: d.tdsSection || null, costCentreId: d.costCentreId === '' ? null : Number(d.costCentreId), itcBlocked: d.itcBlocked.trim() || null,
      };
      return d.id ? alterLedger(d.id, body) : createLedger(body);
    },
    onSuccess: () => { toast.push('Ledger saved', 'success'); setDraft(null); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });

  const delMut = useMutation({
    mutationFn: (id: number) => deleteLedger(id),
    onSuccess: () => { toast.push('Ledger deleted', 'success'); refresh(); },
    onError: (e) => toast.push(apiError(e, 'Delete failed'), 'error'),
  });

  const openAlter = (l: AccLedger) =>
    setDraft({
      id: l.id, name: l.name, alias: l.alias ?? '', groupId: String(l.groupId), opening: l.opening ? String(Math.abs(l.opening)) : '', side: l.opening < 0 ? 'Cr' : 'Dr', partyId: l.partyId,
      gstRate: l.gstRate == null ? '' : String(l.gstRate), tdsSection: l.tdsSection ?? '', costCentreId: l.costCentreId == null ? '' : String(l.costCentreId), itcBlocked: l.itcBlocked ?? '',
    });

  // Expense heads (anywhere under Indirect Expenses) carry the expense bill's defaults.
  const ccQ = useQuery({ queryKey: ['acc-books', 'cost-centres'], queryFn: listCostCentres });
  const secQ = useQuery({ queryKey: ['acc-books', 'tds-sections'], queryFn: listTdsSections });
  const underIndirect = (groupId: string) => {
    const all = groupsQ.data ?? [];
    for (let g = all.find((x) => String(x.id) === groupId), i = 0; g && i < 30; g = all.find((x) => x.id === g!.parentId), i++) if (g.name === 'Indirect Expenses') return true;
    return false;
  };
  const groupOptions = tree.map(({ g, depth }) => <option key={g.id} value={g.id}>{'  '.repeat(depth)}{g.name}</option>);

  return (
    <div>
      <SectionHeader title="Ledgers" />

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:w-[260px]">
          <div className="relative">
            <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, alias, code or group" className="!pl-8" />
          </div>
        </FilterField>
        <FilterField label="Group">
          <Select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">All groups</option>
            {groupOptions}
          </Select>
        </FilterField>
        <FilterField label="Show">
          <Select value={withBalance ? 'bal' : ''} onChange={(e) => setWithBalance(e.target.value === 'bal')}>
            <option value="">All</option>
            <option value="bal">With balance</option>
          </Select>
        </FilterField>
        <div className="col-span-3 flex flex-nowrap gap-1.5 [&>*]:flex-auto sm:col-span-1 sm:ml-auto sm:self-end sm:[&>*]:flex-none">
          <button type="button" className={sideBtn} title="Download the opening balances upload sheet" onClick={() => downloadExcel(OPENING_TEMPLATE_URL, 'GRHAMOY_Opening_Balances_Template.xlsx')}>
            <FileDown size={12} /> Template
          </button>
          <button type="button" className={sideBtn} title="Upload opening balances from Excel" onClick={() => setImportOpen(true)}>
            <Upload size={12} /> Opening balances
          </button>
          <Button variant="primary" size="sm" icon={<Plus size={12} />} onClick={() => setDraft({ id: null, name: '', alias: '', groupId: '', opening: '', side: 'Dr', partyId: null, ...NO_DEFAULTS })}>
            Create Ledger
          </Button>
        </div>
      </FilterBar>

      <Table<AccLedger>
        storageKey="acc-books-ledgers"
        title="Ledgers"
        count={`${rows.length} ledger${rows.length === 1 ? '' : 's'}`}
        rows={rows}
        rowKey={(r) => r.id}
        loading={query.isFetching && all.length === 0}
        empty="No ledgers found."
        columns={[
          {
            key: 'name', header: 'Name', minWidth: '220px',
            cell: (l) => (
              <div>
                <button type="button" className="text-left font-bold text-accent hover:underline" onClick={() => setViewing(l)}>{l.name}</button>
                {(l.alias || l.partyCode) && (
                  <div className="text-[11px] text-[var(--text-muted)]">{[l.alias, l.partyCode].filter(Boolean).join(' · ')}</div>
                )}
              </div>
            ),
          },
          { key: 'group', header: 'Group', width: '180px', cell: (l) => l.group },
          { key: 'opening', header: 'Opening', width: '150px', align: 'right', cell: (l) => drcr(l.opening) },
          { key: 'closing', header: 'Closing', width: '150px', align: 'right', cell: (l) => <strong>{drcr(l.closing)}</strong> },
          { key: 'entries', header: 'Entries', width: '80px', align: 'right', cell: (l) => l.entries },
          {
            key: 'actions', header: '', width: '130px', align: 'right', alwaysVisible: true,
            cell: (l) => (
              <div className="flex items-center justify-end gap-1">
                <Button variant="glass" size="sm" onClick={() => openAlter(l)}>Alter</Button>
                {!l.isSystem && !l.partyId && l.entries === 0 && (
                  <button type="button" onClick={async () => { if (await confirmDlg(`Delete ledger ${l.name}?`)) delMut.mutate(l.id); }} className="rounded p-1.5 text-danger hover:bg-danger/10" title="Delete ledger"><Trash2 size={14} /></button>
                )}
              </div>
            ),
          },
        ]}
      />

      <Modal
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.id ? `Alter Ledger ${draft.name}` : 'Create Ledger'}
        size="sm"
        footer={
          <>
            <Button variant="glass" onClick={() => setDraft(null)}>Cancel</Button>
            <Button variant="primary" disabled={!draft?.name.trim() || !draft?.groupId || saveMut.isPending} onClick={() => draft && saveMut.mutate(draft)}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        {draft && (
          <div className="space-y-3">
            <div>
              <label className={label}>Name</label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} disabled={!!draft.partyId} maxLength={200} />
              {!!draft.partyId && <p className="mt-1 text-[11px] text-[var(--text-muted)]">Name follows Party Master</p>}
            </div>
            <div>
              <label className={label}>Alias</label>
              <Input value={draft.alias} onChange={(e) => setDraft({ ...draft, alias: e.target.value })} maxLength={80} />
            </div>
            <div>
              <label className={label}>Under</label>
              <Select value={draft.groupId} onChange={(e) => setDraft({ ...draft, groupId: e.target.value })}>
                <option value="">Choose group…</option>
                {groupOptions}
              </Select>
            </div>
            <div>
              <label className={label}>Opening balance</label>
              <div className="grid grid-cols-[1fr_80px] gap-2">
                <Input type="number" min={0} value={draft.opening} onChange={(e) => setDraft({ ...draft, opening: e.target.value })} placeholder="0" />
                <Select value={draft.side} onChange={(e) => setDraft({ ...draft, side: e.target.value as 'Dr' | 'Cr' })}>
                  <option value="Dr">Dr</option>
                  <option value="Cr">Cr</option>
                </Select>
              </div>
            </div>
            {underIndirect(draft.groupId) && (
              <div className="rounded-lg border border-[var(--border-color)] p-3">
                <div className="mb-2 text-[11px] font-extrabold uppercase tracking-wider text-[var(--text-muted)]">Expense bill defaults</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={label}>GST rate</label>
                    <Select value={draft.gstRate} onChange={(e) => setDraft({ ...draft, gstRate: e.target.value })}>
                      <option value="">—</option>
                      {[0, 5, 12, 18, 28].map((g) => <option key={g} value={g}>{g ? `${g}%` : 'No GST'}</option>)}
                    </Select>
                  </div>
                  <div>
                    <label className={label}>TDS nature of payment</label>
                    <Select value={draft.tdsSection} onChange={(e) => setDraft({ ...draft, tdsSection: e.target.value })}>
                      <option value="">No TDS</option>
                      {(secQ.data ?? []).map((x) => <option key={x.code} value={x.code}>{x.code} · {x.nature}</option>)}
                    </Select>
                  </div>
                  <div>
                    <label className={label}>Department (cost centre)</label>
                    <Select value={draft.costCentreId} onChange={(e) => setDraft({ ...draft, costCentreId: e.target.value })}>
                      <option value="">—</option>
                      {(ccQ.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Select>
                  </div>
                  <div>
                    <label className={label}>ITC blocked because</label>
                    <Input value={draft.itcBlocked} maxLength={200} placeholder="e.g. Food & beverages — s.17(5)(b)" onChange={(e) => setDraft({ ...draft, itcBlocked: e.target.value })} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {viewing && <LedgerVouchersModal ledger={viewing} onClose={() => setViewing(null)} />}

      <ExcelImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Opening Balances"
        expectedColumns={['Ledger*', 'Group', 'Amount*', 'Dr/Cr*', 'Bill No', 'Bill Date', 'Due Date']}
        columnDetails={[
          { name: 'Ledger', hint: 'Ledger name, alias or party code. A ledger in the file gets the sum of its rows and its opening bills are replaced', example: 'Sharma Interiors' },
          { name: 'Group', hint: 'Needed only to create a ledger that doesn’t exist yet', example: 'Sundry Debtors', optional: true },
          { name: 'Amount', hint: 'Opening amount in ₹', example: '48500' },
          { name: 'Dr/Cr', hint: 'Dr or Cr', example: 'Dr' },
          { name: 'Bill No', hint: 'Only for customer / supplier ledgers — one row per open bill', example: 'INV/24-25/0153', optional: true },
          { name: 'Bill Date', hint: 'DD-MM-YYYY; customer / supplier ledgers only', example: '15-03-2025', optional: true },
          { name: 'Due Date', hint: 'DD-MM-YYYY; customer / supplier ledgers only', example: '14-04-2025', optional: true },
        ]}
        onImport={importOpening}
        onSuccess={refresh}
      />
    </div>
  );
}

function LedgerVouchersModal({ ledger, onClose }: { ledger: AccLedger; onClose: () => void }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const query = useQuery({
    queryKey: ['acc-books', 'ledger-vouchers', ledger.id, from, to],
    queryFn: () => getLedgerVouchers(ledger.id, { from: from || undefined, to: to || undefined }),
  });
  const d = query.data;
  const th = 'px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]';
  const td = 'px-2 py-1.5 align-top';

  return (
    <Modal open onClose={onClose} title={`Ledger Vouchers — ${ledger.name}`} size="lg">
      <div className="mb-3 grid grid-cols-2 gap-2 sm:flex sm:items-end">
        <div>
          <label className={label}>From</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className={label}>To</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <span className="col-span-2 text-[11px] text-[var(--text-muted)] sm:pb-2">{ledger.group}</span>
      </div>
      {query.isLoading ? (
        <p className="py-6 text-center text-sm text-[var(--text-muted)]">Loading…</p>
      ) : query.isError ? (
        <p className="py-6 text-center text-sm text-danger">{apiError(query.error, 'Could not load vouchers')}</p>
      ) : d && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-xs">
            <thead>
              <tr className="border-b border-[var(--border-color)] text-left">
                <th className={th}>Date</th><th className={th}>Particulars</th><th className={th}>Vch Type</th><th className={th}>Vch No</th>
                <th className={`${th} text-right`}>Debit</th><th className={`${th} text-right`}>Credit</th><th className={`${th} text-right`}>Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[var(--border-color)] font-semibold">
                <td className={td} /><td className={td} colSpan={5}>Opening Balance</td><td className={`${td} text-right`}>{drcr(d.opening)}</td>
              </tr>
              {d.rows.map((r, i) => (
                <tr key={`${r.voucherId}-${i}`} className="border-b border-[var(--border-color)]">
                  <td className={`${td} whitespace-nowrap`}>{dmy(r.date)}</td>
                  <td className={td}>
                    {r.particulars}
                    {r.narration && <div className="text-[11px] text-[var(--text-muted)]">{r.narration}</div>}
                  </td>
                  <td className={td}>{r.type}</td>
                  <td className={`${td} whitespace-nowrap font-mono`}>{r.voucherNo}</td>
                  <td className={`${td} text-right`}>{r.debit ? `₹${formatINR(r.debit)}` : ''}</td>
                  <td className={`${td} text-right`}>{r.credit ? `₹${formatINR(r.credit)}` : ''}</td>
                  <td className={`${td} whitespace-nowrap text-right`}>{drcr(r.balance)}</td>
                </tr>
              ))}
              <tr className="font-bold">
                <td className={td} /><td className={td} colSpan={5}>Closing Balance</td><td className={`${td} text-right`}>{drcr(d.closing)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
